<template>
    <section class="sync-review">
        <!-- Portal unreachable: never rendered as an empty list, so an outage can't
             be mistaken for "everything is already synchronized". -->
        <div v-if="connectionError" class="sync-banner sync-banner--error" role="alert">
            <i class="pi pi-times-circle" aria-hidden="true"></i>
            <div class="sync-banner-body">
                <strong>Unable to reach the registration portal database</strong>
                <p>{{ connectionError }}</p>
                <p v-if="lastLoadedAt" class="sync-banner-meta">Last successful load: {{ fmtDate(lastLoadedAt) }}</p>
            </div>
            <button class="btn btn-outline-light btn-sm" type="button" @click="loadPreview()" :disabled="loading">
                Try again
            </button>
        </div>

        <div v-if="syncReport" class="sync-banner" :class="syncReport.errorCount > 0 ? 'sync-banner--warn' : 'sync-banner--ok'">
            <i :class="syncReport.errorCount > 0 ? 'pi pi-exclamation-triangle' : 'pi pi-check-circle'" aria-hidden="true"></i>
            <div class="sync-banner-body">
                <strong>
                    Synchronization finished — {{ syncReport.successCount }} succeeded,
                    {{ syncReport.errorCount }} failed
                </strong>
                <ul v-if="syncReport.errorDetails && syncReport.errorDetails.length" class="sync-report-errors">
                    <li v-for="(detail, i) in syncReport.errorDetails" :key="i">
                        <span class="sync-report-name">{{ detail.portalMemberModel.name }}</span>
                        <span class="sync-report-reason">{{ detail.error }}</span>
                    </li>
                </ul>
            </div>
            <button class="btn btn-sm btn-close-report" type="button" @click="syncReport = null" aria-label="Dismiss report">
                <i class="pi pi-times" aria-hidden="true"></i>
            </button>
        </div>

        <div class="sync-stats">
            <div class="stat-tile">
                <span class="stat-value">{{ preview.totalFromPortal }}</span>
                <span class="stat-label">In portal</span>
            </div>
            <div class="stat-tile stat-tile--ok">
                <span class="stat-value">{{ preview.readyCount }}</span>
                <span class="stat-label">Ready to sync</span>
            </div>
            <div class="stat-tile stat-tile--warn">
                <span class="stat-value">{{ preview.incompleteCount }}</span>
                <span class="stat-label">Incomplete data</span>
            </div>
            <div class="stat-tile stat-tile--error">
                <span class="stat-value">{{ preview.blockedCount }}</span>
                <span class="stat-label">Needs fixing</span>
            </div>
        </div>

        <div class="sync-toolbar">
            <div class="sync-tabs" role="tablist">
                <button
                    v-for="tab in tabs"
                    :key="tab.key"
                    type="button"
                    role="tab"
                    class="sync-tab"
                    :class="{ 'is-active': activeTab === tab.key }"
                    :aria-selected="activeTab === tab.key"
                    @click="setTab(tab.key)"
                >{{ tab.label }} ({{ tab.count }})</button>
            </div>

            <div class="sync-toolbar-right">
                <input
                    v-model="searchText"
                    type="search"
                    class="form-control"
                    placeholder="Search name, email, organization"
                    aria-label="Search delegates"
                    @input="currentPage = 1"
                >
                <button class="btn btn-outline-secondary" type="button" @click="loadPreview()" :disabled="loading || syncing">
                    <i class="pi pi-refresh" :class="{ 'is-spinning': loading }" aria-hidden="true"></i>
                    <span>Refresh</span>
                </button>
            </div>
        </div>

        <div class="sync-meta" aria-live="polite">
            <span v-if="loading">Loading delegates from the registration portal…</span>
            <span v-else-if="lastLoadedAt">Last loaded {{ fmtDate(lastLoadedAt) }}</span>
        </div>

        <div class="sync-table-wrap">
            <table class="table sync-table">
                <thead>
                <tr>
                    <th scope="col" class="select-column">
                        <input
                            type="checkbox"
                            aria-label="Select all eligible delegates"
                            :checked="allVisibleSelected"
                            :disabled="selectableVisible.length === 0"
                            @change="toggleSelectAll($event)"
                        >
                    </th>
                    <th scope="col">Name</th>
                    <th scope="col">Email</th>
                    <th scope="col">Organization</th>
                    <th scope="col">Payment</th>
                    <th scope="col">Status</th>
                    <th scope="col" class="expand-column"><span class="sr-only">Details</span></th>
                </tr>
                </thead>

                <tbody>
                <tr v-if="loading">
                    <td colspan="7">
                        <div class="sync-table-state">
                            <i class="pi pi-spinner is-spinning" aria-hidden="true"></i>
                            <span>Loading…</span>
                        </div>
                    </td>
                </tr>

                <tr v-else-if="filteredItems.length === 0">
                    <td colspan="7">
                        <div class="sync-table-state sync-table-state--empty">
                            <i class="pi pi-check-circle" aria-hidden="true"></i>
                            <strong>{{ emptyTitle }}</strong>
                            <span>{{ emptyMessage }}</span>
                        </div>
                    </td>
                </tr>

                <template v-for="item in pagedItems" :key="item.member.id">
                    <tr :class="{ 'is-blocked': !item.canSync }">
                        <td class="select-column">
                            <input
                                type="checkbox"
                                :aria-label="`Select ${item.member.name}`"
                                :disabled="!item.canSync"
                                :checked="selectedIds.includes(item.member.id)"
                                @change="toggleOne(item.member.id)"
                            >
                        </td>
                        <td class="delegate-name">
                            {{ item.member.name }}
                            <small v-if="item.generatedUsername">Login ID: <code>{{ item.generatedUsername }}</code></small>
                        </td>
                        <td>{{ displayValue(item.member.email) }}</td>
                        <td>{{ displayValue(item.member.organizationName) }}</td>
                        <td>{{ fmtDateOnly(item.member.paymentDate) }}</td>
                        <td>
                            <span v-if="!item.canSync" class="badge badge-blocked">
                                <i class="pi pi-times-circle" aria-hidden="true"></i>
                                {{ item.errorCount }} blocking
                            </span>
                            <span v-else-if="item.warningCount > 0" class="badge badge-warn">
                                <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
                                {{ item.warningCount }} field{{ item.warningCount === 1 ? '' : 's' }} missing
                            </span>
                            <span v-else class="badge badge-ok">
                                <i class="pi pi-check" aria-hidden="true"></i>
                                Ready
                            </span>
                        </td>
                        <td class="expand-column">
                            <button
                                type="button"
                                class="expand-button"
                                :aria-expanded="expandedIds.includes(item.member.id)"
                                :aria-label="`Toggle details for ${item.member.name}`"
                                @click="toggleExpand(item.member.id)"
                            >
                                <i class="pi" :class="expandedIds.includes(item.member.id) ? 'pi-chevron-up' : 'pi-chevron-down'" aria-hidden="true"></i>
                            </button>
                        </td>
                    </tr>

                    <tr v-if="expandedIds.includes(item.member.id)" class="detail-row">
                        <td colspan="7">
                            <div class="detail-panel">
                                <ul v-if="item.issues.length" class="issue-list">
                                    <li
                                        v-for="(issue, i) in item.issues"
                                        :key="i"
                                        :class="isError(issue) ? 'issue-error' : 'issue-warning'"
                                    >
                                        <i :class="isError(issue) ? 'pi pi-times-circle' : 'pi pi-exclamation-triangle'" aria-hidden="true"></i>
                                        <span>{{ issue.message }}</span>
                                    </li>
                                </ul>

                                <dl class="detail-grid">
                                    <div v-for="field in detailFields(item.member)" :key="field.label" class="detail-field">
                                        <dt>{{ field.label }}</dt>
                                        <dd :class="{ 'is-empty': !field.value }">{{ field.value || '— empty' }}</dd>
                                    </div>
                                </dl>
                            </div>
                        </td>
                    </tr>
                </template>
                </tbody>
            </table>
        </div>

        <div class="sync-footer">
            <Paginator
                v-if="filteredItems.length > pageSize"
                :rows="pageSize"
                :totalRecords="filteredItems.length"
                :first="(currentPage - 1) * pageSize"
                @page="onPageChange"
                template="PrevPageLink PageLinks NextPageLink"
            />
            <span v-else class="sync-footer-spacer"></span>

            <div class="sync-actions">
                <span class="selection-count">{{ selectedIds.length }} selected</span>
                <button
                    class="btn btn-primary"
                    type="button"
                    :disabled="selectedIds.length === 0 || syncing || loading"
                    @click="onSync()"
                >
                    <i class="pi pi-sync" :class="{ 'is-spinning': syncing }" aria-hidden="true"></i>
                    <span>{{ syncing ? 'Synchronizing…' : `Synchronize (${selectedIds.length})` }}</span>
                </button>
            </div>
        </div>

        <ConfirmDialog></ConfirmDialog>
    </section>
</template>

<script>
import {defineComponent} from 'vue'
import apiService from "@/services/ApiService";
import moment from "moment";

const SEVERITY_ERROR = 1;

export default defineComponent({
    name: "MemberSyncReview",
    data() {
        return {
            preview: {
                items: [],
                totalFromPortal: 0,
                readyCount: 0,
                blockedCount: 0,
                incompleteCount: 0
            },
            selectedIds: [],
            expandedIds: [],
            searchText: '',
            activeTab: 'all',
            currentPage: 1,
            pageSize: 10,
            loading: false,
            syncing: false,
            connectionError: null,
            syncReport: null,
            lastLoadedAt: null
        }
    },
    computed: {
        tabs() {
            return [
                {key: 'all', label: 'All', count: this.preview.totalFromPortal},
                {key: 'ready', label: 'Ready', count: this.preview.readyCount},
                {key: 'blocked', label: 'Needs fixing', count: this.preview.blockedCount}
            ];
        },
        filteredItems() {
            const search = this.searchText.trim().toLowerCase();

            return this.preview.items.filter(item => {
                if (this.activeTab === 'ready' && !item.canSync) return false;
                if (this.activeTab === 'blocked' && item.canSync) return false;

                if (!search) return true;

                const member = item.member;
                return [member.name, member.email, member.organizationName]
                    .some(value => (value || '').toLowerCase().includes(search));
            });
        },
        pagedItems() {
            const start = (this.currentPage - 1) * this.pageSize;
            return this.filteredItems.slice(start, start + this.pageSize);
        },
        selectableVisible() {
            return this.filteredItems.filter(item => item.canSync);
        },
        allVisibleSelected() {
            return this.selectableVisible.length > 0
                && this.selectableVisible.every(item => this.selectedIds.includes(item.member.id));
        },
        emptyTitle() {
            if (this.connectionError) return 'Delegates could not be loaded';
            if (this.searchText || this.activeTab !== 'all') return 'No delegates match this filter';
            return 'All delegates are already synchronized';
        },
        emptyMessage() {
            if (this.connectionError) return 'Fix the connection to the registration portal, then try again.';
            if (this.searchText || this.activeTab !== 'all') return 'Try a different search term or switch tabs.';
            return 'New delegates will appear here once they have registered and paid in the portal.';
        }
    },
    methods: {
        isError(issue) {
            return issue.severity === SEVERITY_ERROR;
        },
        displayValue(value) {
            return value || '—';
        },
        fmtDate(date) {
            return moment(date).format('DD/MM/YYYY HH:mm:ss');
        },
        fmtDateOnly(date) {
            return date ? moment(date).format('DD/MM/YYYY') : '—';
        },
        detailFields(member) {
            return [
                {label: 'Name', value: member.name},
                {label: 'Title / badge name', value: member.preferredBadgeName},
                {label: 'Gender', value: this.genderLabel(member.gender)},
                {label: 'Email', value: member.email},
                {label: 'Phone number', value: member.phoneNumber},
                {label: 'Designation', value: member.designation},
                {label: 'Organization', value: member.organizationName},
                {label: 'Organization description', value: member.organizationDescription},
                {label: 'Address', value: member.address},
                {label: 'City', value: member.cityName},
                {label: 'Country', value: member.countryName},
                {label: 'Website', value: member.website},
                {label: 'Shirt size', value: member.shirtSize},
                {label: 'Food preference', value: member.foodTypeName},
                {label: 'Payment method', value: member.paymentMethodName},
                {label: 'Payment date', value: member.paymentDate ? this.fmtDateOnly(member.paymentDate) : null}
            ];
        },
        genderLabel(gender) {
            if (gender === 1) return 'Male';
            if (gender === 2) return 'Female';
            return null;
        },
        setTab(key) {
            this.activeTab = key;
            this.currentPage = 1;
        },
        toggleExpand(id) {
            const index = this.expandedIds.indexOf(id);
            if (index === -1) {
                this.expandedIds.push(id);
            } else {
                this.expandedIds.splice(index, 1);
            }
        },
        toggleOne(id) {
            const index = this.selectedIds.indexOf(id);
            if (index === -1) {
                this.selectedIds.push(id);
            } else {
                this.selectedIds.splice(index, 1);
            }
        },
        toggleSelectAll(event) {
            const visibleIds = this.selectableVisible.map(item => item.member.id);

            if (event.target.checked) {
                const merged = new Set([...this.selectedIds, ...visibleIds]);
                this.selectedIds = Array.from(merged);
            } else {
                this.selectedIds = this.selectedIds.filter(id => !visibleIds.includes(id));
            }
        },
        loadPreview() {
            this.loading = true;
            this.connectionError = null;

            return apiService.get('/member/sync/preview')
                .then(response => {
                    this.preview = response.data;
                    this.lastLoadedAt = new Date();
                    this.currentPage = 1;
                    this.expandedIds = [];
                    // Pre-select everything eligible: the common path is "review, then accept".
                    this.selectedIds = this.preview.items
                        .filter(item => item.canSync)
                        .map(item => item.member.id);
                })
                .catch(error => {
                    console.log(error);
                    this.preview = {items: [], totalFromPortal: 0, readyCount: 0, blockedCount: 0, incompleteCount: 0};
                    this.selectedIds = [];
                    this.connectionError = error.response?.data?.Message
                        || error.response?.data?.message
                        || 'An unexpected error occurred while contacting the API.';
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        onSync() {
            this.$confirm.require({
                message: `${this.selectedIds.length} delegate(s) will be transferred into the application database. Continue?`,
                header: 'Synchronize members',
                icon: 'pi pi-sync',
                accept: () => {
                    this.doSync();
                }
            });
        },
        doSync() {
            this.syncing = true;
            this.syncReport = null;

            return apiService.post('/member/sync', {memberRefIds: this.selectedIds})
                .then(response => {
                    this.syncReport = response.data;

                    this.$toast.add({
                        severity: response.data.errorCount > 0 ? 'warn' : 'success',
                        summary: response.data.errorCount > 0 ? 'Finished with errors' : 'Success',
                        detail: `Total: ${response.data.totalData}, successful: ${response.data.successCount}, failed: ${response.data.errorCount}.`,
                        life: 5000
                    });

                    return this.loadPreview();
                })
                .catch(error => {
                    console.log(error);
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Failed to synchronize',
                        detail: error.response?.data?.Message || 'An error occurred',
                        life: 5000
                    });
                })
                .finally(() => {
                    this.syncing = false;
                });
        },
        onPageChange(event) {
            this.currentPage = event.page + 1;
            this.pageSize = event.rows;
        }
    },
    mounted() {
        this.loadPreview();
    }
})
</script>

<style scoped>
.sync-review { overflow: hidden; border: 1px solid #e5e7eb; border-radius: 0.75rem; background: #fff; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04); }
.sync-banner { display: flex; align-items: flex-start; gap: 0.75rem; padding: 1rem 1.25rem; border-bottom: 1px solid #edf0f5; font-size: 0.875rem; }
.sync-banner > i { margin-top: 0.15rem; font-size: 1.1rem; }
.sync-banner-body { flex: 1; }
.sync-banner-body p { margin: 0.25rem 0 0; }
.sync-banner-meta { opacity: 0.75; font-size: 0.8125rem; }
.sync-banner--error { background: #fef2f2; color: #991b1b; }
.sync-banner--error .btn { border-color: #f5b5b5; background: #fff; color: #b42318; }
.sync-banner--ok { background: #f0fdf4; color: #166534; }
.sync-banner--warn { background: #fffbeb; color: #92400e; }
.btn-close-report { padding: 0.15rem 0.4rem; border: 0; background: transparent; color: inherit; opacity: 0.6; }
.btn-close-report:hover { opacity: 1; }
.sync-report-errors { margin: 0.5rem 0 0; padding: 0; list-style: none; }
.sync-report-errors li { display: flex; gap: 0.5rem; padding: 0.2rem 0; }
.sync-report-name { min-width: 12rem; font-weight: 700; }
.sync-report-reason { opacity: 0.9; }
.sync-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; border-bottom: 1px solid #edf0f5; background: #edf0f5; }
.stat-tile { display: flex; flex-direction: column; gap: 0.15rem; padding: 1.1rem 1.25rem; background: #fff; }
.stat-value { color: #1f2937; font-size: 1.6rem; font-weight: 700; line-height: 1.1; }
.stat-label { color: #6b7280; font-size: 0.75rem; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; }
.stat-tile--ok .stat-value { color: #16a34a; }
.stat-tile--warn .stat-value { color: #d97706; }
.stat-tile--error .stat-value { color: #dc2626; }
.sync-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 1rem 1.25rem; border-bottom: 1px solid #edf0f5; }
.sync-tabs { display: flex; gap: 0.35rem; }
.sync-tab { padding: 0.4rem 0.8rem; border: 1px solid #d7dce5; border-radius: 999px; background: #fff; color: #4b5563; font-size: 0.8125rem; font-weight: 600; }
.sync-tab.is-active { border-color: #3656bf; background: #eef2ff; color: #3656bf; }
.sync-toolbar-right { display: flex; align-items: center; gap: 0.5rem; }
.sync-toolbar-right .form-control { min-width: 16rem; }
.sync-toolbar-right .btn { display: inline-flex; align-items: center; gap: 0.4rem; white-space: nowrap; }
.sync-meta { padding: 0.7rem 1.25rem; color: #6b7280; font-size: 0.8125rem; }
.sync-table-wrap { overflow-x: auto; border-top: 1px solid #edf0f5; }
.sync-table { min-width: 62rem; margin: 0; color: #374151; font-size: 0.875rem; }
.sync-table thead th { padding: 0.8rem 1rem; border-top: 0; border-bottom: 1px solid #e5e7eb; background: #f8fafc; color: #6b7280; font-size: 0.6875rem; font-weight: 700; letter-spacing: 0.045em; text-transform: uppercase; white-space: nowrap; }
.sync-table tbody td { padding: 0.9rem 1rem; border-color: #edf0f5; vertical-align: middle; }
.sync-table tbody tr.is-blocked { background: #fffbfb; }
.select-column, .expand-column { width: 3rem; text-align: center; }
.delegate-name { min-width: 14rem; color: #1f2937; font-weight: 700; }
.delegate-name small { display: block; margin-top: 0.2rem; color: #9ca3af; font-weight: 400; font-size: 0.75rem; }
.delegate-name code { padding: 0; background: transparent; color: #6b7280; }
.badge { display: inline-flex; align-items: center; gap: 0.3rem; padding: 0.3rem 0.55rem; border-radius: 0.375rem; font-size: 0.75rem; font-weight: 600; white-space: nowrap; }
.badge-ok { background: #f0fdf4; color: #166534; }
.badge-warn { background: #fffbeb; color: #92400e; }
.badge-blocked { background: #fef2f2; color: #991b1b; }
.expand-button { display: inline-flex; width: 2rem; height: 2rem; align-items: center; justify-content: center; border: 1px solid #d7dce5; border-radius: 0.375rem; background: #fff; color: #4b5563; }
.expand-button:hover { border-color: #9eaceb; background: #f1f4ff; color: #3656bf; }
.detail-row > td { padding: 0 !important; background: #fafbff; }
.detail-panel { padding: 1.1rem 1.35rem; border-left: 3px solid #c7d2fe; }
.issue-list { margin: 0 0 1rem; padding: 0; list-style: none; }
.issue-list li { display: flex; align-items: flex-start; gap: 0.5rem; padding: 0.28rem 0; font-size: 0.8125rem; }
.issue-list li > i { margin-top: 0.1rem; }
.issue-error { color: #b42318; }
.issue-warning { color: #92400e; }
.detail-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr)); gap: 0.75rem 1.5rem; margin: 0; }
.detail-field dt { color: #9ca3af; font-size: 0.6875rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase; }
.detail-field dd { margin: 0.1rem 0 0; color: #374151; font-size: 0.8125rem; word-break: break-word; }
.detail-field dd.is-empty { color: #d1d5db; font-style: italic; }
.sync-table-state { display: flex; min-height: 9rem; align-items: center; justify-content: center; gap: 0.6rem; color: #6b7280; }
.sync-table-state--empty { flex-direction: column; gap: 0.35rem; text-align: center; }
.sync-table-state--empty > i { margin-bottom: 0.25rem; color: #9ca3af; font-size: 1.5rem; }
.sync-table-state--empty strong { color: #374151; font-size: 0.95rem; }
.sync-table-state--empty span { font-size: 0.8125rem; }
.sync-footer { position: sticky; bottom: 0; display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 0.9rem 1.25rem; border-top: 1px solid #edf0f5; background: #fff; }
.sync-footer-spacer { flex: 1; }
.sync-actions { display: flex; align-items: center; gap: 0.9rem; }
.selection-count { color: #6b7280; font-size: 0.8125rem; font-weight: 600; }
.sync-actions .btn { display: inline-flex; min-height: 2.6rem; align-items: center; gap: 0.45rem; padding: 0 1rem; border-radius: 0.5rem; font-size: 0.875rem; font-weight: 600; }
.is-spinning { animation: sync-spin 0.9s linear infinite; }
@keyframes sync-spin { to { transform: rotate(360deg); } }
@media (max-width: 991.98px) {
    .sync-stats { grid-template-columns: repeat(2, 1fr); }
    .sync-toolbar { flex-direction: column; align-items: stretch; }
    .sync-toolbar-right { flex-wrap: wrap; }
    .sync-toolbar-right .form-control { min-width: 0; flex: 1; }
}
@media (max-width: 575.98px) {
    .sync-footer { flex-direction: column; align-items: stretch; }
    .sync-actions { justify-content: space-between; }
}
</style>
